import type { Project } from './store'

export type ProjectReadiness = 'attention' | 'setup' | 'progress' | 'deployed' | 'new'

export interface ProjectSignals {
  connections: Array<{ name: string; type?: string }>
  connectionNames: string[]
  connectorTypes: string[]
  readiness: ProjectReadiness
  statusLabel: string
  statusDetail: string
  needsSetup: boolean
  connectionCount: number
  conversationCount: number
  activeConversationCount: number
  lastActivityAt?: string
}

const CONNECTOR_LABELS: Record<string, string> = {
  AD: 'AD / LDAP',
  LDAP: 'AD / LDAP',
  DIRECTORY: 'AD / LDAP',
  DATABASE: 'JDBC',
  DB: 'JDBC',
  JDBC: 'JDBC',
  REST: 'REST',
  SOAP: 'SOAP',
  FILE: 'File',
  CSV: 'File',
  IDENTITY: 'File',
  SCHEMAUSERJOB: 'File',
}

function connectorLabel(value: string): string {
  const normalized = value.trim().toUpperCase()
  if (!normalized || normalized === 'UNKNOWN') return ''
  return CONNECTOR_LABELS[normalized] || value.trim()
}

export function deriveProjectSignals(project: Project): ProjectSignals {
  const statusCounts = project.connection_status_counts || {}
  const connectionCount = project.connection_count ?? Object.values(statusCounts).reduce((sum, count) => sum + count, 0)
  const conversationCount = project.conversation_count ?? 0
  const activeConversationCount = project.active_conversation_count ?? 0
  const needsSetup = !project.saviynt_url || (project.secret_count ?? 0) === 0
  const failed = statusCounts.failed || (connectionCount === 0 && project.status === 'failed' ? 1 : 0)
  const deployed = statusCounts.deployed || (connectionCount === 0 && project.status === 'deployed' ? 1 : 0)
  const inProgress = (statusCounts.draft || 0) + (statusCounts.saved || 0)

  let readiness: ProjectReadiness
  let statusLabel: string
  let statusDetail: string
  if (failed > 0) {
    readiness = 'attention'
    statusLabel = 'Needs attention'
    statusDetail = `${failed} failed connection${failed === 1 ? '' : 's'}`
  } else if (needsSetup) {
    readiness = 'setup'
    statusLabel = 'Setup required'
    statusDetail = !project.saviynt_url ? 'Add a Saviynt tenant URL' : 'Add project credentials'
  } else if (connectionCount === 0) {
    readiness = 'new'
    statusLabel = 'Ready to start'
    statusDetail = 'Create the first connection'
  } else if (deployed === connectionCount && connectionCount > 0) {
    readiness = 'deployed'
    statusLabel = 'Deployed'
    statusDetail = `${connectionCount} deployed connection${connectionCount === 1 ? '' : 's'}`
  } else {
    readiness = 'progress'
    statusLabel = 'In progress'
    statusDetail = `${inProgress || connectionCount - deployed} connection${(inProgress || connectionCount - deployed) === 1 ? '' : 's'} in progress`
  }

  const sourceFamilies = project.connector_families?.length
    ? project.connector_families
    : [project.connection_type || ''].filter(Boolean)
  const connectorTypes = Array.from(new Set(sourceFamilies.map(connectorLabel).filter(Boolean))).slice(0, 3)
  const seenConnections = new Set<string>()
  const connections = (project.connection_summaries || []).flatMap(connection => {
    const name = connection.name.trim()
    if (!name) return []
    const type = connectorLabel(connection.connector_family || '') || undefined
    const key = `${name.toLowerCase()}\u0000${(type || '').toLowerCase()}`
    if (seenConnections.has(key)) return []
    seenConnections.add(key)
    return [{ name, type }]
  })
  const connectionNames = connections.length
    ? connections.map(connection => connection.name)
    : Array.from(new Set((project.connection_names || []).map(name => name.trim()).filter(Boolean)))

  return {
    connections,
    connectionNames,
    connectorTypes,
    readiness,
    statusLabel,
    statusDetail,
    needsSetup,
    connectionCount,
    conversationCount,
    activeConversationCount,
    lastActivityAt: project.last_activity_at || project.updated_at || project.created_at,
  }
}

export function projectActivityLabel(value?: string, now = Date.now()): string {
  if (!value) return 'No activity yet'
  const timestamp = new Date(value).getTime()
  if (!Number.isFinite(timestamp)) return 'Activity date unavailable'
  const elapsed = Math.max(0, now - timestamp)
  const minutes = Math.floor(elapsed / 60_000)
  if (minutes < 1) return 'Updated just now'
  if (minutes < 60) return `Updated ${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `Updated ${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `Updated ${days}d ago`
  return `Updated ${new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}
